import {useCallback, useLayoutEffect, useRef, useState} from "react";
import PartnerIndustryRules from '../../../core/services/partner-industry-rules';

/**
 * Applies partner-specific default Nexus Search orderBy after schema load,
 * unless the user (or a search token) has already set an explicit sort.
 *
 * Exposes `isOrderByReady` so the search page can hold Products until the default
 * has been applied (avoids a Search with null sortBy followed by a second Search).
 *
 * @param {object} params
 * @param {string|undefined|null} params.partnerId
 * @param {{entries?: Array<{id: string, name: string, sortable?: boolean}>}} params.searchSchema
 * @param {boolean} params.loadingSearchSchema
 * @param {{id: string, isDescending: boolean}|null|undefined} params.orderBy
 * @param {function(*): void} params.setOrderBy
 * @param {boolean} [params.isTokenLoading]
 */
const usePartnerDefaultNexusSearchOrderBy = ({
                                                 partnerId,
                                                 searchSchema,
                                                 loadingSearchSchema,
                                                 orderBy,
                                                 setOrderBy: setOrderByRaw,
                                                 isTokenLoading = false,
                                             }) => {
    const skipApplyRef = useRef(false);
    const [isOrderByReady, setIsOrderByReady] = useState(false);

    /**
     * Resolves the partner default orderBy for the current schema.
     * @type {function(): ({id: string, isDescending: boolean}|null)}
     */
    const resolveDefault = useCallback(() => {
        return PartnerIndustryRules.resolveDefaultNexusSearchOrderBy(partnerId, searchSchema?.entries ?? []);
    }, [partnerId, searchSchema?.entries]);

    /**
     * Marks that sort was set by the user or a search token so the default effect will not overwrite it.
     * @type {function(): void}
     */
    const markSortInteraction = useCallback(() => {
        skipApplyRef.current = true;
    }, []);

    /**
     * Clears the skip flag so the default effect can re-apply (e.g. on industry change).
     * @type {function(): void}
     */
    const resetSortInteraction = useCallback(() => {
        skipApplyRef.current = false;
        setIsOrderByReady(false);
    }, []);

    /**
     * User-facing setOrderBy: records interaction and updates state.
     * @type {function(*): void}
     */
    const setOrderBy = useCallback((nextOrderBy) => {
        skipApplyRef.current = true;
        setOrderByRaw(nextOrderBy);
    }, [setOrderByRaw]);

    /**
     * Resets orderBy to the partner default (used by clear-all filters).
     * @type {function(): void}
     */
    const resetOrderByToDefault = useCallback(() => {
        skipApplyRef.current = false;
        setOrderByRaw(resolveDefault());
    }, [resolveDefault, setOrderByRaw]);

    /**
     * Applies partner default when schema is ready and no user/token sort interaction.
     * Uses layout effect so orderBy settles before Products paint / Search fires.
     */
    useLayoutEffect(() => {
        if (loadingSearchSchema || isTokenLoading) {
            setIsOrderByReady(false);
            return;
        }
        if (!searchSchema?.entries?.length) {
            setIsOrderByReady(true);
            return;
        }
        if (skipApplyRef.current) {
            setIsOrderByReady(true);
            return;
        }

        const defaultOrderBy = resolveDefault();
        const isSame =
            (defaultOrderBy === null && (orderBy === null || orderBy === undefined))
            || (
                !!defaultOrderBy
                && !!orderBy
                && defaultOrderBy.id === orderBy.id
                && defaultOrderBy.isDescending === orderBy.isDescending
            );
        if (!isSame) {
            setOrderByRaw(defaultOrderBy);
            return;
        }

        setIsOrderByReady(true);
    }, [
        loadingSearchSchema,
        isTokenLoading,
        searchSchema?.entries,
        resolveDefault,
        setOrderByRaw,
        orderBy,
    ]);

    return {
        setOrderBy,
        resetOrderByToDefault,
        resetSortInteraction,
        markSortInteraction,
        isOrderByReady,
    };
};

export default usePartnerDefaultNexusSearchOrderBy;
